import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { toZonedDateTime } from '@ms-rewards/date-lib';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { DateTimePicker } from '@components/date-picker';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { getTimezoneOptions } from '@components/utils/time';
import { injectTSDI } from '../../../../../core/tsdi';
import { I18n } from '../../../../../core/i18n';
import { useStore } from './store';

interface AddFacilityVisitProps {
    open?: boolean;
    onModalClose?(): void;
    onSuccess?: () => void;
}

export const AddFacilityVisit = observer(
    ({
        open: initialOpen = false,
        onModalClose,
        onSuccess
    }: AddFacilityVisitProps) => {
        const [open, setOpen] = useState(initialOpen);
        const { fields, errors, onSubmitHandler, addFacilityVisitRequest } =
            useStore(() => {
                handleClose();
                onSuccess?.();
            });
        const { __ } = injectTSDI(I18n);

        const { from, to, facilityId, facilityName, timeZone } = fields;

        const handleClose = () => {
            setOpen(false);
            onModalClose?.();
        };

        useEffect(() => {
            setOpen(initialOpen);
        }, [initialOpen]);

        const handleSubmit = async () => {
            await onSubmitHandler();
        };

        return (
            <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
                <DialogContent className="sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Add Facility Visit</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 md:grid-cols-3">
                        <DateTimePicker
                            label="Start date"
                            value={from.value ? from.value.toDate() : null}
                            onChange={(value) => {
                                if (value == null) {
                                    return;
                                }
                                void from.onChange(
                                    toZonedDateTime(value, timeZone.value)
                                );
                            }}
                        />
                        <DateTimePicker
                            label="End date"
                            value={to.value ? to.value.toDate() : null}
                            onChange={(value) => {
                                if (value == null) {
                                    return;
                                }
                                void to.onChange(
                                    toZonedDateTime(value, timeZone.value)
                                );
                            }}
                            minDate={
                                from.value ? from.value.toDate() : undefined
                            }
                        />
                        <Select
                            label="Timezone"
                            value={timeZone.value}
                            options={getTimezoneOptions()}
                            onChange={(value) => {
                                timeZone.onChange(value);
                            }}
                        />
                        <div className="md:col-span-3 grid gap-4 md:grid-cols-2">
                            <TextField
                                value={facilityId.value}
                                onChange={facilityId.onChange}
                                type="text"
                                label="Facility id (can be taken from Facilities tab)"
                                required
                                error={
                                    facilityId.error &&
                                    __(facilityId.error as I18nKey, 0)
                                }
                            />
                            <TextField
                                value={facilityName.value}
                                onChange={facilityName.onChange}
                                type="text"
                                label="Facility name (can be taken from Facilities tab)"
                                required
                                error={
                                    facilityName.error &&
                                    __(facilityName.error as I18nKey, 0)
                                }
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            disabled={addFacilityVisitRequest.loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            loading={addFacilityVisitRequest.loading}
                            disabled={Boolean(errors)}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
);
